import { TFunction } from 'next-i18next';
import { ApiTalentPageV3Ok, ApiSpokenLanguagesArr, ApiSpokenLanguageLevelsObj } from '@type/v1-api-types';
import { ExtendedSWRInfiniteResponse, InfinitePageType, PaginationPageType } from '@type/swr';
import {
  Dispatch, SetStateAction, useState, useCallback, useMemo,
} from 'react';
import Head from 'next/head';
import useResizeObserver from 'use-resize-observer';
import { useAppDispatch, useAppSelector } from 'src/app';
import { updateApplicantTalentList } from '@store/reducers/employer/lists/listsReducer';
import { getApplicantTalentList } from '@store/reducers/employer/lists/listsSelector';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { TalentCardMemo, TalentCardFormProps } from '@components/templates/employer/cards-lists/TalentCard';
import { CvDetailHub } from '@components/templates/cv/CvDetailHub';
import {
  Button, InfiniteList, PageCounter, Modal,
} from '@components/common';
import { BaseTrackingEventData } from '@type/tracking';
import { useUrlChange } from '@utils/url/useUrlChange';
import { LanguagesListAndLevelsContext } from '@components/contexts/LanguagesListAndLevels';

// Interfaces
// ******************************************
// Define a constraint for the generic type
interface WithResumeItems {
  items?: ApiSchema<'ResumeCardData'>[];
  page?: number;
  cursor?: string;
  apiRequestId?: string;
}

// Update PickedSwrHookProps to be generic
interface PickedSwrHookProps<T extends WithResumeItems = ApiTalentPageV3Ok> extends Omit<ExtendedSWRInfiniteResponse<T, unknown>, | 'error' | 'mutate' | 'isValidating' | 'mutateSiblings'> {}

interface TalentListProps<T extends WithResumeItems = ApiTalentPageV3Ok, P extends InfinitePageType = 'page'> extends PickedSwrHookProps<T> {
  isValidating?: boolean,
  jobSlug?: string,
  isPassiveRecommendation?: boolean,
  jobInfo?: ApiSchema<'ListReadJob'>,
  trackingData? : BaseTrackingEventData;
  t: TFunction,
  setCurrentPage: Dispatch<SetStateAction<PaginationPageType<P>>>;
  applicant?: TalentCardFormProps,
  autoload?: number,
  locale: Locale,
  paginationType?: P,
  spokenLanguagesList?: ApiSpokenLanguagesArr,
  spokenLanguagesLevels?: ApiSpokenLanguageLevelsObj
}

/**
 * @description Common applicants list for job context (/job/applicants, /job/candidates, /job/users-who-favorite-my-job, etc.)
 * It uses the TalentCardMemo component to render the cards. The interface for 'data' is an object extending WithResumeItems, default is ApiTalentPageV3Ok.
 *
 * - **jobSlug**: string - is the job slug. Don't forget to pass it if you're using this component in the job (applicants) context.
 * - **jobInfo**: ApiSchema<'ListReadJob'> - is the job information. It is used to pass the job info to the TalentCardMemo component.
 * - **t**: TFunction - is the translation function
 * - **data**: Object extending WithResumeItems, default ApiTalentPageV3Ok[] - is the data fetched from the server with SwrInfinite
 * - **trackingData**: Object for tracking details
 * - **setSize**: Dispatch<SetStateAction<number>> - is the function to set the size of the infinite list (get it from the SwrInfinite hook)
 * - **size**: number - is the size of the infinite list (get it from the SwrInfinite hook)
 * - **setCurrentPage**: Dispatch<SetStateAction<number>> - is the function to set the current page inside useFiltersQueryParams
 * - **showLoadMore**: boolean - is the flag to show the load more button (get it from the SwrInfinite hook)
 * - **isEmpty**: boolean - is the flag to show the empty message (get it from the SwrInfinite hook)
 * - **isLoading**: boolean - is the flag to show the loading spinner (get it from the SwrInfinite hook)
 * - **isValidating**: boolean - is the flag to show if the *data* is being revalidated (get it from the SwrInfinite hook)
 * - **sizeAsRef**: RefObject<number> - is the ref for the size of the infinite list (get it from the SwrInfinite hook)
 * - **applicant**: TalentCardFormProps - is the props for the TalentCardMemo component. It is used to pass useBjForm functions
 * needed for bulk operations.
 * - **mutatePage**: SwrInfiniteMutateType<DataType> - The SWR infinite list mutatePage function.
 * - **autoload**: number - is the number of pages to autoload. Default is 3.
 * - **locale**: Locale - The current app locale.
 *
 * Applicant object:
 * - applicant.record: *RecordInterface* - The record function from useBjForm. Required.
 * - applicant.name: *string* - The name of the form group. Required.
 * - applicant.setSelectAll: *Dispatch<SetStateAction<boolean | undefined>>* - The function to set the 'select all' checkbox. Optional. Default is undefined.
 */
export const TalentList = <T extends WithResumeItems = ApiTalentPageV3Ok, P extends InfinitePageType = 'page'>(props: TalentListProps<T, P>) => {
  // Destructure props
  const {
    jobSlug,
    jobInfo,
    isPassiveRecommendation,
    t,
    data,
    setSize,
    trackingData,
    size,
    setCurrentPage,
    showLoadMore,
    isEmpty,
    isLoading,
    isValidating,
    sizeAsRef,
    applicant,
    mutatePage,
    autoload = 3,
    locale,
    paginationType = 'page' as P,
    spokenLanguagesList,
    spokenLanguagesLevels,
  } = props;


  // Resize observer. Need to watch the container for the infinite list in order to do proper calculations for pagination.
  // *************************************
  const { ref: parentRef, width: parentWidth } = useResizeObserver<HTMLDivElement>({
    round: Math.floor,
  });

  // Check out the different user functions in header info data (internalAgent, etc.) and pass that info to the card,
  // so that the card can display 'special' actions.
  // *************************************
  const headerInfoData = useAppSelector(getUserHeaderInfo);


  // REDUX.
  // *****************************************
  const appDispatch = useAppDispatch();
  const listState = useAppSelector(getApplicantTalentList);

  // Current list URL
  // *************************************
  const [currentListUrl, setCurrentListUrl] = useState<string | undefined>(undefined);

  // Get current query parameters
  // *************************************
  const selectedCvSlug = listState?.selectedCvSlug;

  const languagesContext = useMemo(
    () => ({
      spokenLanguagesList,
      spokenLanguagesLevels,
    }),
    [spokenLanguagesList, spokenLanguagesLevels],
  );


  const checkSelectedCvSlug = useCallback((url: string) => {
    const absoluteUrl = url.startsWith('http')
      ? url
      : `${window.location.origin}${url}`;

    return new URL(absoluteUrl).searchParams.get('selectedCvSlug');
  }, []);


  useUrlChange((newUrl: string) => {
    const cvSlug = checkSelectedCvSlug(newUrl);
    // When modal is opened via URL change (e.g., browser navigation),
    // we need to set currentListUrl so the modal can close properly
    if (cvSlug && !currentListUrl) {
      const url = new URL(newUrl, window.location.origin);
      url.searchParams.delete('selectedCvSlug');
      const baseUrl = url.searchParams.toString()
        ? `${url.origin}${url.pathname}?${url.searchParams.toString()}`
        : `${url.origin}${url.pathname}`;
      setCurrentListUrl(baseUrl);
    }

    if (cvSlug !== selectedCvSlug) {
      // Logic for stable URL management
      // If parent list re-renders and re-syncs URL (shallow push) but misses our slug,
      // we try to put it back to avoid premature closing of the modal.
      const nextJsOptions = window.history.state?.options;
      const isShallow = nextJsOptions?.shallow || window.history.state?.shallow;

      if (!cvSlug && selectedCvSlug && isShallow) {
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}?${new URLSearchParams({
            ...Object.fromEntries(new URLSearchParams(window.location.search)),
            selectedCvSlug,
          }).toString()}`,
        );
        return;
      }

      appDispatch(updateApplicantTalentList({
        ...(listState ?? {}),
        selectedCvSlug: cvSlug,
        modalOpen: !!cvSlug,
      }));
    }
  });


  // Logic for the CV Detail Modal
  // The context is used in ResumeArrowsNavigation
  // *********************************************************************************************

  // Open talent modal
  const openTalentModal = useCallback((slug: string) => {
    setCurrentListUrl(window.location.href);
    appDispatch(updateApplicantTalentList({
      ...(listState ?? {}),
      selectedCvSlug: slug,
      modalOpen: true,
    }));

    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${new URLSearchParams({
        ...Object.fromEntries(new URLSearchParams(window.location.search)),
        selectedCvSlug: slug,
      }).toString()}`,
    );
  }, [appDispatch, listState]);

  const closeTalentModal = useCallback(() => {
    appDispatch(updateApplicantTalentList({
      ...(listState ?? {}),
      modalOpen: false,
      // We don't set selectedCvSlug to null here yet to allow for the modal leave transition.
    }));

    // Use currentListUrl if available, otherwise construct the URL from current location
    const baseUrl = currentListUrl || window.location.href;
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.delete('selectedCvSlug');

    const search = url.searchParams.toString();
    const nextUrl = search
      ? `${url.pathname}?${search}`
      : url.pathname;

    window.history.replaceState(null, '', nextUrl);
  }, [appDispatch, listState, currentListUrl]);

  const afterModalLeave = useCallback(() => {
    // Clear the selected CV slug from Redux after the modal transition has finished.
    appDispatch(updateApplicantTalentList({
      ...(listState ?? {}),
      selectedCvSlug: null,
      modalOpen: false,
    }));
  }, [appDispatch, listState]);

  // REDUX, navbar URL and history updates before modal Enter/Leave actions.
  // *************************************
  const beforeModalEnter = () => {
    void setCurrentListUrl(window.location.href);
  };


  // Page refresh variables & classes
  // *************************************
  const isRefreshing = isValidating && !isLoading;
  const containerClasses = `grid gap-4 my-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6 xl:my-6 ${isRefreshing ? 'opacity-80 saturate-50' : ''}`;
  const coverClasses = 'absolute inset-0 cursor-wait z-10';


  // Render component
  // *************************************
  return (
    <>
      {/* Infinite list */}
      <InfiniteList
        autoload={autoload}
        infiniteProps={{
          setSize,
          isEmpty,
          showLoadMore,
          sizeAsRef,
          isLoading,
        }}
        isEmptyLabels={{
          isEmptyTitle: 'no_results',
          isEmptyText: 'cv.search.message.no_result',
        }}
        renderList={() => (
          <div ref={parentRef} className={containerClasses}>
            { /* Show the cover when refreshing */
              isRefreshing && <div className={coverClasses} />
            }

            { /* Render the talent cards */
              data?.map((pageGroup, index) => {
                let viewportPage: PaginationPageType<P>;
                if (paginationType === 'cursor') {
                  // When cursor pagination is used, the cursor for current page was contained by the previous page
                  const prevPageCursor = index === 0 ? null : data[index - 1].cursor;
                  viewportPage = (prevPageCursor ?? '') as PaginationPageType<P>;
                } else {
                  viewportPage = (pageGroup.page ?? 1) as PaginationPageType<P>;
                }

                return (
                  <PageCounter
                    pageGroup={pageGroup}
                    currentPage={viewportPage}
                    callback={setCurrentPage}
                    key={`page-${viewportPage}`}
                    parentWidth={parentWidth}
                    paginationType={paginationType}
                  >
                    {pageGroup?.items?.map((item, position) => {
                      const { internalAgent } = headerInfoData ?? {}; // Destructure to avoid redundant optional chaining

                      return (
                        <TalentCardMemo
                          cardData={item}
                          key={item.id} // Ensure each card has a unique key
                          internalAgent={internalAgent}
                          jobSlug={jobSlug}
                          jobInfo={jobInfo}
                          trackingData={{
                            ...(trackingData?.listType && { listType: trackingData?.listType }),
                            ...(pageGroup?.apiRequestId && { listUuid: pageGroup?.apiRequestId }),
                            position,
                          }}
                          applicant={applicant || undefined} // Use applicant directly or undefined if falsy
                          showActions={!!headerInfoData || !!jobSlug}
                          revalidatePage={(userSlug: string) => {
                            void mutatePage((d) => !!d.items?.find((swrItem) => swrItem.slug === userSlug));
                          }}
                          openTalentModal={openTalentModal}
                          trackingId={pageGroup?.apiRequestId}
                          locale={locale}
                        />
                      );
                    })}

                  </PageCounter>
                );
              })
            }
          </div>
        )}
        renderButton={() => (
          <div className="my-8 text-center">
            <Button
              isLoading={isLoading}
              rounding="full"
              color="secondary"
              onClick={() => { void setSize(size + 1); }}
            >
              {t('dashboard.label.notification_show')}
            </Button>
          </div>
        )}
      />


      {/* CV Detail Modal */}
      {selectedCvSlug && (
        <>
          { // Set the CV detail title.
            <Head>
              <title>{t(jobSlug ? 'cv.detail.meta.title' : 'cv.detail.meta.title')}</title>
            </Head>
          }
          <Modal
            isOpen={!!listState?.modalOpen}
            onClose={closeTalentModal}
            beforeEnter={beforeModalEnter}
            afterLeave={afterModalLeave}
            size="full"
            className="!transform-none !overflow-visible"
            backdropClickDisabled
            body={(
              <LanguagesListAndLevelsContext.Provider value={languagesContext}>
                <CvDetailHub
                  isPassiveRecommendation={isPassiveRecommendation}
                  slug={selectedCvSlug}
                  jobSlug={jobSlug}
                  isAuthenticated={!!headerInfoData || !!jobSlug}
                  closeModal={closeTalentModal}
                  queryParams={currentListUrl?.split('?')[1]}
                  swrTalentList={data}
                  openTalentModal={openTalentModal}
                  showLoadMore={showLoadMore}
                  size={size}
                  setSize={setSize}
                />
              </LanguagesListAndLevelsContext.Provider>
            )}
          />
        </>
      )}
    </>
  );
};
